import React, { useState } from "react";
import { CURRENCY } from "../constants";
import {
  useCbeDepositMutation,
  useTelebirrDepositMutation,
  useArifMpesaDespositMutation,
} from "../store/apis";
import { useSelector } from "react-redux";
import { selectUser } from "../store/auth-slice";
import { handleApiError } from "../errors";
import { toastService } from "../screens/notificationPage/ToastContainer";

const telebirrImg =
  "https://dash.bet/files/operators/additional_img/teleBirr.svg";
const cbeImg =
  "https://is1-ssl.mzstatic.com/image/thumb/Purple126/v4/d1/4c/78/d14c7876-5624-5e61-4ec6-2f7ca50338c3/AppIcon-0-0-1x_U007emarketing-0-0-0-7-0-0-sRGB-0-0-0-GLES2_U002c0-512MB-85-220-0-0.png/512x512bb.jpg";
const mpesaImg =
  "https://tse4.mm.bing.net/th/id/OIP.Ba3yksT-VX-5AeF8aQPJsQHaEK?rs=1&pid=ImgDetMain";

export const DepositModal = ({ onClose }) => {
  const [depositAmount, setDepositAmount] = useState(100);
  const user = useSelector(selectUser);
  const [phoneNumber, setPhoneNumber] = useState(user?.username || "");

  // Payment method mutations
  const [telebirrDeposit, { isLoading: isTelebirrLoading }] =
    useTelebirrDepositMutation();
  const [cbeDeposit, { isLoading: isCbeLoading }] = useCbeDepositMutation();
  const [mpesaDeposit, { isLoading: isMpesaLoading }] =
    useArifMpesaDespositMutation();

  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState("telebirr"); // 'telebirr', 'cbe', or 'mpesa'

  // Quick amount options
  const quickAmounts = [100, 200, 500, 1000, 2000, 5000];

  const handleDeposit = async () => {
    if (isNaN(depositAmount) || depositAmount <= 0) {
      toastService.error("Please enter a valid deposit amount");
      return;
    }

    try {
      let response;

      switch (paymentMethod) {
        case "telebirr":
          response = await telebirrDeposit({
            items: [
              {
                name: "Wallet Deposit",
                quantity: 1,
                price: depositAmount,
                description: "Top-up to user wallet.",
                image: telebirrImg,
              },
            ],
            phone: phoneNumber,
            lang: "EN",
          }).unwrap();
          break;
        case "cbe":
          response = await cbeDeposit({
            items: [
              {
                name: "Wallet Deposit",
                quantity: 1,
                price: depositAmount,
                description: "Top-up to user wallet.",
                image: cbeImg,
              },
            ],
            phone: phoneNumber,
            lang: "EN",
          }).unwrap();
          break;
        case "mpesa":
          response = await mpesaDeposit({
            items: [
              {
                name: "Wallet Deposit",
                quantity: 1,
                price: depositAmount,
                description: "Top-up to user wallet.",
                image: mpesaImg,
              },
            ],
            phone: phoneNumber,
            lang: "EN",
          }).unwrap();
          break;
        default:
          toastService.error("Invalid payment method");
      }

      toastService.success(response.msg || "Deposit initiated successfully");
      onClose();
    } catch (error) {
      handleApiError(error, {
        showToast: true,
        toastPosition: "top-right",
      });
    }
  };

  const handleQuickAmount = (amount) => {
    setDepositAmount(amount);
  };

  const isLoading = isTelebirrLoading || isCbeLoading || isMpesaLoading;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-2 sm:p-4 overflow-y-auto">
      <div className="bg-[#0C1B22] rounded-xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto no-scrollbar">
        {/* Modal Header */}
        <div className="sticky top-0 bg-[#0C1B22] p-3 sm:p-4 flex justify-between items-center border-b border-gray-700 z-10">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white">
            DEPOSIT METHODS
          </h2>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white text-xl sm:text-2xl p-1"
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-3 sm:p-4 md:p-6">
          {/* Payment Method Selector */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-gray-300 mb-2 sm:mb-3 text-sm md:text-base">
              Select Payment Method
            </label>
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 sm:gap-3">
              <button
                onClick={() => setPaymentMethod("telebirr")}
                className={`p-2 sm:p-3 rounded-lg flex items-center justify-center ${
                  paymentMethod === "telebirr"
                    ? "bg-[#1E3A8A] border border-yellow"
                    : "bg-[#162A3A]"
                }`}
              >
                <img
                  src={telebirrImg}
                  alt="Telebirr"
                  className="h-5 sm:h-6 md:h-8 mr-2"
                />
                <span className="text-xs md:text-sm text-white">TELEBIRR</span>
              </button>
              <button
                onClick={() => setPaymentMethod("cbe")}
                className={`p-2 sm:p-3 rounded-lg flex items-center justify-center ${
                  paymentMethod === "cbe"
                    ? "bg-[#1E3A8A] border border-yellow"
                    : "bg-[#162A3A]"
                }`}
              >
                <img
                  src={cbeImg}
                  alt="CBE"
                  className="h-5 sm:h-6 md:h-8 mr-2"
                />
                <span className="text-xs md:text-sm text-white">CBE</span>
              </button>
              <button
                onClick={() => setPaymentMethod("mpesa")}
                className={`p-2 sm:p-3 rounded-lg flex items-center justify-center xs:col-span-2 sm:col-auto ${
                  paymentMethod === "mpesa"
                    ? "bg-[#1E3A8A] border border-yellow"
                    : "bg-[#162A3A]"
                }`}
              >
                <img
                  src={mpesaImg}
                  alt="Mpesa"
                  className="h-5 sm:h-6 md:h-8 mr-2"
                />
                <span className="text-xs md:text-sm text-white">MPESA</span>
              </button>
            </div>
          </div>

          {/* Phone Number Input */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-gray-300 mb-1 sm:mb-2 text-sm md:text-base">
              Phone Number
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full p-2 sm:p-3 text-sm md:text-base border border-gray-600 rounded-lg bg-[#162A3A] text-white focus:ring-2 focus:ring-yellow focus:border-yellow"
              placeholder="Enter phone number"
            />
          </div>

          {/* Amount Input */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-gray-300 mb-1 sm:mb-2 text-sm md:text-base">
              Amount
            </label>
            <div className="relative">
              <input
                type="number"
                value={depositAmount}
                onChange={(e) => setDepositAmount(Number(e.target.value))}
                className="w-full p-2 sm:p-3 text-sm md:text-base border border-gray-600 rounded-lg bg-[#162A3A] text-white focus:ring-2 focus:ring-yellow focus:border-yellow pl-8 sm:pl-10 md:pl-12"
                placeholder="Enter amount"
                min="0"
              />
              <span className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-300 text-sm md:text-base">
                {CURRENCY}
              </span>
            </div>
          </div>

          {/* Quick Amount Buttons */}
          <div className="mb-4 sm:mb-6">
            <label className="block text-gray-300 mb-1 sm:mb-2 text-sm md:text-base">
              Quick Amounts
            </label>
            <div className="grid grid-cols-3 gap-1 sm:gap-2">
              {quickAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => handleQuickAmount(amount)}
                  className={`py-1 sm:py-2 rounded-lg text-xs md:text-sm ${
                    depositAmount === amount
                      ? "bg-yellow text-[#0C1B22] font-bold"
                      : "bg-[#162A3A] text-white hover:bg-[#1E3A8A]"
                  }`}
                >
                  {CURRENCY}
                  {amount}
                </button>
              ))}
            </div>
          </div>

          {/* Continue Button */}
          <button
            onClick={handleDeposit}
            disabled={isLoading}
            className={`w-full py-2 sm:py-3 px-4 rounded-lg font-bold text-sm md:text-base ${
              isLoading
                ? "bg-gray-600 cursor-not-allowed"
                : "bg-yellow text-[#0C1B22] hover:bg-yellow-dark"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 md:h-5 md:w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </span>
            ) : (
              "CONTINUE"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
